import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, HardHat, ShieldCheck, Clock, XCircle, Crown, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { company: "", trades: "", country: "", city: "", bio: "", website: "", phone: "", years_experience: "" };

const POLICY = [
  "Hold an active paid Costobuild membership (VIP, Pro or Business).",
  "Provide a real company name, contact details and the trades you cover.",
  "The Costobuild team manually reviews every application before listing.",
  "Approved contractors carry the Vetted badge and can receive quote requests from homeowners.",
  "Listings that receive repeated complaints or go inactive may be removed.",
];

export default function ContractorApply() {
  const navigate = useNavigate();
  const { billing } = useBilling();
  const [profile, setProfile] = useState(undefined);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const isPaid = billing && billing.plan !== "free";

  useEffect(() => {
    api.get("/contractors/me").then(({ data }) => {
      if (data.contractor_id) {
        setProfile(data);
        setForm({
          company: data.company || "", trades: (data.trades || []).join(", "), country: data.country || "",
          city: data.city || "", bio: data.bio || "", website: data.website || "", phone: data.phone || "",
          years_experience: data.years_experience ?? "",
        });
      } else setProfile(null);
    }).catch(() => setProfile(null));
  }, []);

  const submit = async () => {
    if (form.company.trim().length < 2) return toast.error("Enter your company name.");
    setSaving(true);
    try {
      const { data } = await api.post("/contractors/apply", {
        ...form,
        trades: form.trades.split(",").map((t) => t.trim()).filter(Boolean),
        years_experience: form.years_experience === "" ? null : Number(form.years_experience),
      });
      setProfile(data);
      toast.success(data.status === "approved" ? "Profile updated" : "Application submitted — we'll review it shortly");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Could not submit application.";
      toast.error(msg);
      if (e.response?.status === 402) navigate("/billing");
    } finally {
      setSaving(false);
    }
  };

  if (profile === undefined)
    return <div className="min-h-screen bp-grid"><Navbar /><div className="flex justify-center py-32"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div></div>;

  const statusCard = profile && (
    profile.status === "approved" ? (
      <div className="mt-6 flex items-center gap-3 border border-green-500/50 bg-green-500/10 px-4 py-3" data-testid="contractor-status-approved">
        <CheckCircle2 className="h-5 w-5 text-green-400" />
        <div>
          <div className="font-heading text-sm font-semibold">You're live in the directory</div>
          <p className="font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">Vetted · homeowners can request quotes from you</p>
        </div>
        <Link to="/contractors" className="ml-auto font-mono2 text-[11px] uppercase tracking-widest text-primary hover:underline">View listing →</Link>
      </div>
    ) : profile.status === "rejected" ? (
      <div className="mt-6 flex items-center gap-3 border border-red-500/50 bg-red-500/10 px-4 py-3" data-testid="contractor-status-rejected">
        <XCircle className="h-5 w-5 text-red-400" />
        <div>
          <div className="font-heading text-sm font-semibold">Application not approved</div>
          <p className="font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">Update your profile below and re-submit anytime</p>
        </div>
      </div>
    ) : (
      <div className="mt-6 flex items-center gap-3 border border-amber-500/50 bg-amber-500/10 px-4 py-3" data-testid="contractor-status-pending">
        <Clock className="h-5 w-5 text-amber-400" />
        <div>
          <div className="font-heading text-sm font-semibold">Application under review</div>
          <p className="font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">The Costobuild team is vetting your profile</p>
        </div>
      </div>
    )
  );

  const lbl = "font-mono2 text-xs uppercase tracking-widest text-muted-foreground";
  const fieldCls = "mt-1.5 rounded-sm border-border bg-transparent";

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-8">
        <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary"><HardHat className="mr-2 inline h-3.5 w-3.5" />For Contractors</p>
        <h1 className="mt-2 font-heading text-4xl font-extrabold tracking-tighter sm:text-5xl">Get listed. Get quoting.</h1>
        <p className="mt-3 text-muted-foreground">
          Join the Costobuild contractor directory and receive quote requests from homeowners with AI-scoped,
          blueprint-ready projects.
        </p>

        {statusCard}

        {!isPaid && !profile && (
          <div className="mt-6 flex items-center gap-3 border border-primary/50 bg-primary/10 px-4 py-3" data-testid="contractor-paid-gate">
            <Crown className="h-5 w-5 text-primary" />
            <div className="text-sm">
              A paid membership (VIP, Pro or Business) is required to be listed.{" "}
              <Link to="/billing" className="font-semibold text-primary hover:underline">Upgrade →</Link>
            </div>
          </div>
        )}

        <div className="mt-8 border border-border p-6">
          <h2 className="font-heading text-lg font-bold">{profile ? "Your directory profile" : "Apply for listing"}</h2>
          <div className="mt-5 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className={lbl}>Company name</Label><Input data-testid="apply-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={fieldCls} /></div>
              <div><Label className={lbl}>Trades (comma separated)</Label><Input data-testid="apply-trades" value={form.trades} onChange={(e) => setForm({ ...form, trades: e.target.value })} className={fieldCls} placeholder="General contracting, Framing, Electrical" /></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div><Label className={lbl}>Country</Label><Input data-testid="apply-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={fieldCls} /></div>
              <div><Label className={lbl}>City</Label><Input data-testid="apply-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={fieldCls} /></div>
              <div><Label className={lbl}>Years of experience</Label><Input data-testid="apply-years" type="number" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} className={fieldCls} /></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className={lbl}>Website</Label><Input data-testid="apply-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={fieldCls} placeholder="yourcompany.com" /></div>
              <div><Label className={lbl}>Phone</Label><Input data-testid="apply-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={fieldCls} /></div>
            </div>
            <div><Label className={lbl}>About your company</Label><Textarea data-testid="apply-bio" rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className={fieldCls} placeholder="What you build, where you work, what makes you great…" /></div>
            <Button onClick={submit} disabled={saving} data-testid="apply-submit"
              className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {profile ? "Update Profile" : "Submit Application"}
            </Button>
          </div>
        </div>

        <div className="mt-8 border border-border p-6" data-testid="vetting-policy">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-bold">Vetting policy</h2>
          </div>
          <ul className="mt-4 space-y-2">
            {POLICY.map((p, i) => (
              <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                <span className="font-mono2 text-primary">{String(i + 1).padStart(2, "0")}</span> {p}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Footer />
    </div>
  );
}
